import React, { useState, useEffect } from 'react';
import { Plus, Play, Pause, XCircle, Edit, Trash2, RefreshCw, Zap, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { useToast } from '../../../components/ui/use-toast';
import BackButton from '../../../components/admin/BackButton';
import CreateSubscriptionModal from '../../../components/admin/finance/CreateSubscriptionModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Subscriptions = () => {
  const { toast } = useToast();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, paused, cancelled
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('subscriptions'); // subscriptions, history
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [generatingNow, setGeneratingNow] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
    fetchSchedulerStatus();
  }, [filter]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchInvoiceHistory();
    }
  }, [activeTab]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const params = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      
      const response = await axios.get(`${API_URL}/api/finance/subscriptions/`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch subscriptions",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/finance/subscriptions/scheduler-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  const fetchInvoiceHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/finance/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { auto_generated: true }
      });
      setInvoiceHistory(response.data);
    } catch (error) {
      console.error('Error fetching invoice history:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch invoice history",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNow = async () => {
    try {
      setGeneratingNow(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_URL}/api/finance/subscriptions/generate-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: response.data.message || "Invoice generation completed",
      });
      
      // Refresh data
      fetchSubscriptions();
      if (activeTab === 'history') {
        fetchInvoiceHistory();
      }
    } catch (error) {
      console.error('Error generating invoices:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to generate invoices",
      });
    } finally {
      setGeneratingNow(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      paused: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Paused' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly'
    };
    return labels[frequency] || frequency;
  };

  const handleStatusChange = async (subscriptionId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      await axios.patch(
        `${API_URL}/api/finance/subscriptions/${subscriptionId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Success",
        description: `Subscription ${newStatus === 'active' ? 'resumed' : newStatus}`,
      });
      
      fetchSubscriptions();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to update subscription status",
      });
    }
  };

  const handleDelete = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to delete this subscription? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      
      await axios.delete(`${API_URL}/api/finance/subscriptions/${subscriptionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Subscription deleted successfully",
      });
      
      fetchSubscriptions();
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete subscription",
      });
    }
  };

  const calculateTotal = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((sum, item) => {
      return sum + (item.quantity * parseFloat(item.unit_price));
    }, 0);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <BackButton />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#2B2D42]">Recurring Invoices</h1>
          <p className="text-gray-600 mt-1">Manage subscription-based invoice automation</p>
        </div>
        
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Subscription
        </Button>
      </div>

      {/* Scheduler Status Card */}
      <Card className="p-6 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Clock className="w-8 h-8 text-[#00B4D8]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#2B2D42]">APScheduler Status</h3>
              {schedulerStatus && (
                <div className="mt-1">
                  <div className="flex items-center gap-2">
                    {schedulerStatus.running ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-600">Running</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-semibold text-red-600">Stopped</span>
                      </>
                    )}
                  </div>
                  {schedulerStatus.jobs && schedulerStatus.jobs.length > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      Next run: <strong>{new Date(schedulerStatus.jobs[0].next_run_time).toLocaleString('en-US')}</strong>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <Button
            onClick={handleGenerateNow}
            disabled={generatingNow}
            className="bg-gradient-to-r from-[#00B4D8] to-[#023E8A] hover:from-[#0096c7] hover:to-[#012a5e] text-white"
          >
            <Zap className="w-4 h-4 mr-2" />
            {generatingNow ? 'Generating...' : 'Generate Now'}
          </Button>
        </div>
      </Card>

      {/* Tabs */}
      <Card className="p-4 mb-6">
        <div className="flex gap-2 border-b pb-3 mb-3">
          <Button
            variant={activeTab === 'subscriptions' ? "default" : "ghost"}
            onClick={() => setActiveTab('subscriptions')}
            className={activeTab === 'subscriptions' ? "bg-[#00B4D8] hover:bg-[#0096c7]" : ""}
          >
            Subscriptions
          </Button>
          <Button
            variant={activeTab === 'history' ? "default" : "ghost"}
            onClick={() => setActiveTab('history')}
            className={activeTab === 'history' ? "bg-[#00B4D8] hover:bg-[#0096c7]" : ""}
          >
            Invoice History
          </Button>
        </div>

        {/* Filters (only for subscriptions tab) */}
        {activeTab === 'subscriptions' && (
          <div className="flex gap-2">
            {['all', 'active', 'paused', 'cancelled'].map((status) => (
              <Button
                key={status}
                variant={filter === status ? "default" : "outline"}
                onClick={() => setFilter(status)}
                className={filter === status ? "bg-[#00B4D8] hover:bg-[#0096c7]" : ""}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
            
            <Button
              variant="outline"
              onClick={fetchSubscriptions}
              className="ml-auto"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        )}
      </Card>

      {/* Content - Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading subscriptions...</div>
          ) : subscriptions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No subscriptions found. Create your first subscription to automate recurring invoices.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-[#00B4D8] to-[#023E8A]">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Client</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Frequency</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Next Invoice</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Auto-Send</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscriptions.map((subscription) => (
                    <tr key={subscription.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold text-gray-900">{subscription.client_name}</div>
                        <div className="text-xs text-gray-500">{subscription.client_id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {getFrequencyLabel(subscription.frequency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {subscription.items?.[0]?.currency || 'USD'} {calculateTotal(subscription.items).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {new Date(subscription.next_invoice_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(subscription.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {subscription.auto_send ? (
                          <span className="text-green-600 font-semibold">✓ Yes</span>
                        ) : (
                          <span className="text-gray-400">✗ No</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          {subscription.status === 'active' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(subscription.id, 'paused')}
                              title="Pause"
                            >
                              <Pause className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {subscription.status === 'paused' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(subscription.id, 'active')}
                              className="text-green-600 border-green-600 hover:bg-green-50"
                              title="Resume"
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {subscription.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(subscription.id, 'cancelled')}
                              className="text-red-600 border-red-600 hover:bg-red-50"
                              title="Cancel"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(subscription.id)}
                            className="text-red-600 border-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Content - Invoice History Tab */}
      {activeTab === 'history' && (
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading invoice history...</div>
          ) : invoiceHistory.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No auto-generated invoices found. Invoices will appear here after the scheduler runs or when you click "Generate Now".
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-[#023E8A] to-[#00B4D8]">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Invoice ID</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Client</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Subscription</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Generated On</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoiceHistory.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-mono text-sm text-gray-900">{invoice.invoice_number || invoice.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {invoice.client_name || invoice.client_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invoice.subscription_id ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                            Auto-Generated
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {new Date(invoice.created_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {invoice.currency || 'USD'} {(invoice.total_amount || invoice.grand_total || invoice.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                          invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {invoice.status || 'draft'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Create Subscription Modal */}
      <CreateSubscriptionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchSubscriptions}
      />
    </div>
  );
};

export default Subscriptions;
